import React, { useState, useEffect, useRef, useMemo } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, 
  CheckCircle2, 
  Phone,
  Mail,
  Ban,
  X,
  User,
  Building2,
  FileBadge,
  FileText,
  Trophy,
  Send,
  Loader2,
  Paperclip,
  AlertTriangle,
  Mic,
  CheckSquare,
  MessageCircle,
  RefreshCw,
  Tag,
  Pencil,
  TrendingUp,
  Target,
  Star,
  Zap
} from 'lucide-react';
import { BrokerGamification } from '@/types';
import { GamificationBadge } from '@/components/common/GamificationBadge';
import calcaBranca from '@/assets/calca_branca.png';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const getAuthHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const rawToken = localStorage.getItem('va_token') || '';
  return rawToken ? { Authorization: rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}` } : {};
};
const getAuthToken = (): string => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('va_token') || '';
};

interface CorretoresPageProps {
  darkMode: boolean;
  initialBrokerId?: number | null;
}

interface Broker {
  id: number;
  name: string;
  agency: string;
  agencyId?: number | null;
  phone: string;
  email: string;
  creci: string;
  cargo?: 'SOCIO' | 'GERENTE' | 'CORRETOR' | 'CLIENTE';
  agencyStatus?: string | null;
  agencyActive?: boolean | null;
  status: 'active' | 'blocked';
  bio?: string;
  gamificacao?: BrokerGamification;
  fotoUrl?: string | null;
  possuiWhatsapp?: boolean | null;
  cvcrmId?: string | null;
}

interface BrokerMessage {
  id: number | string;
  body: string;
  direction: 'IN' | 'OUT' | 'SYS';
  createdAt: string;
  mediaType?: string | null;
  mediaUrl?: string | null;
  atendimentoId?: number | null;
  whatsappMsgId?: string | null;
}

interface BrokerAtendimento {
  id: number;
  status: string;
  contato_nome?: string | null;
  contato_whatsapp?: string | null;
  produto_nome?: string | null;
  imobiliaria_nome?: string | null;
  categoria_nome?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

interface AuditLogEntry {
  id: number;
  level?: string;
  event?: string;
  service?: string;
  createdAt?: string;
  data?: any;
}

interface BrokerKpis {
  corretorId: number;
  chamadosTotal?: number;
  chamadosAbertos?: number;
  leadsRecebidos: number | null;
  visitas: number | null;
  vgv: number | null;
}

type TagOption = { id: number; name: string };

interface CloseCategory {
  id: number;
  nome: string;
}

interface CloseProduct {
  id: number;
  nome: string;
}

interface AgencyOption {
  id: number;
  name: string;
  pracaId?: number | null;
  pracaNome?: string | null;
  pracaIds?: number[];
}

interface PracaOption {
  id: number;
  nome: string;
}


export const CorretoresPage: React.FC<CorretoresPageProps> = ({ darkMode, initialBrokerId }) => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>(initialBrokerId ? 'detail' : 'list');
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  const defaultTitleRef = useRef<string | null>(null);
  const [brokenPhotoIds, setBrokenPhotoIds] = useState<Set<number>>(() => new Set());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profileTab, setProfileTab] = useState<'ficha' | 'conversa' | 'leads'>('ficha');
  const [chatMessages, setChatMessages] = useState<BrokerMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatCursor, setChatCursor] = useState<number | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatShowLoadMore, setChatShowLoadMore] = useState(false);
  const [activeAtendimentoId, setActiveAtendimentoId] = useState<number | null>(null);
  const [activeAtendimentoStatus, setActiveAtendimentoStatus] = useState<string | null>(null);
  const [activeAtendimentoLoading, setActiveAtendimentoLoading] = useState(false);
  const [activeAtendimentoError, setActiveAtendimentoError] = useState<string | null>(null);
  const [activeAtendimentoClosing, setActiveAtendimentoClosing] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closeData, setCloseData] = useState({ productId: '', category: '', reason: '' });
  const [closeCategories, setCloseCategories] = useState<CloseCategory[]>([]);
  const [closeProducts, setCloseProducts] = useState<CloseProduct[]>([]);
  const [conversaTab, setConversaTab] = useState<'conversa' | 'atendimentos'>('conversa');
  const [brokerTickets, setBrokerTickets] = useState<BrokerAtendimento[]>([]);
  const [brokerTicketsLoading, setBrokerTicketsLoading] = useState(false);
  const [brokerTicketsError, setBrokerTicketsError] = useState<string | null>(null);
  const [brokerTicketsPage, setBrokerTicketsPage] = useState(1);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!defaultTitleRef.current) {
      defaultTitleRef.current = document.title;
    }
    return () => {
      if (defaultTitleRef.current) {
        document.title = defaultTitleRef.current;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (viewMode === 'detail' && selectedBroker?.name) {
      document.title = `CRT | ${selectedBroker.name}`;
      return;
    }
    if (defaultTitleRef.current) {
      document.title = defaultTitleRef.current;
    }
  }, [viewMode, selectedBroker?.name]);
  const [brokerTicketsHasMore, setBrokerTicketsHasMore] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<BrokerAtendimento | null>(null);
  const [ticketMessages, setTicketMessages] = useState<BrokerMessage[]>([]);
  const [ticketMessagesLoading, setTicketMessagesLoading] = useState(false);
  const [ticketMessagesError, setTicketMessagesError] = useState<string | null>(null);
  const [brokerAuditLogs, setBrokerAuditLogs] = useState<AuditLogEntry[]>([]);
  const [brokerAuditLogsLoading, setBrokerAuditLogsLoading] = useState(false);
  const [brokerAuditLogsError, setBrokerAuditLogsError] = useState<string | null>(null);
  const [brokerAuditLogsOffset, setBrokerAuditLogsOffset] = useState(0);
  const [brokerAuditLogsHasMore, setBrokerAuditLogsHasMore] = useState(true);
  const [brokerAuditLogsLoadingMore, setBrokerAuditLogsLoadingMore] = useState(false);
  const brokerAuditLogsPageSize = 50;
  const [brokerKpis, setBrokerKpis] = useState<BrokerKpis | null>(null);
  const [brokerKpisLoading, setBrokerKpisLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [brokerTags, setBrokerTags] = useState<TagOption[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [tagSuccess, setTagSuccess] = useState<string | null>(null);
  const tagSnapshotRef = useRef<string[]>([]);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatPendingScrollRef = useRef<{ height: number; top: number } | null>(null);
  const chatShouldScrollToBottomRef = useRef(false);
  const chatAtendimentoIdsRef = useRef<Set<number>>(new Set());
  const chatInitialLoadRef = useRef(true);
  const CHAT_PAGE_SIZE = 120;
  const BROKER_TICKETS_PAGE_SIZE = 5;
  const [chatAttachment, setChatAttachment] = useState<{ file: File; previewUrl: string; mime: string; name: string } | null>(null);
  const [chatUploading, setChatUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  
  // Agency List State
  const [agenciesList, setAgenciesList] = useState<AgencyOption[]>([]);
  const [pracas, setPracas] = useState<PracaOption[]>([]);
  const [pracasLoading, setPracasLoading] = useState(false);
  const [isFichaModalOpen, setIsFichaModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isConversaModalOpen, setIsConversaModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [syncOptions, setSyncOptions] = useState({ whatsapp: false, cvcrm: false });
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [infoAgencySearch, setInfoAgencySearch] = useState('');
  const [infoForm, setInfoForm] = useState<{
    pracaId: number | null;
    agencyId: number | null;
    creci: string;
    cargo?: Broker['cargo'];
    cvcrmId: string;
  }>({
    pracaId: null,
    agencyId: null,
    creci: '',
    cargo: 'CORRETOR',
    cvcrmId: '',
  });

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Broker>>({
    name: '',
    agency: '',
    phone: '',
    email: '',
    creci: '',
    status: 'active'
  });

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const id = setTimeout(() => {
      setPage(1);
      setSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const normalizePhotoUrl = (raw: unknown) => {
    if (!raw || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://')) return `https://${trimmed.slice(7)}`;
    return trimmed;
  };

  const resolveCargo = (item: any): Broker['cargo'] => {
    const rawCargo = (item?.cargo || '').toString().trim().toUpperCase();
    if (rawCargo === 'SOCIO' || rawCargo === 'GERENTE' || rawCargo === 'CORRETOR' || rawCargo === 'CLIENTE') {
      return rawCargo as Broker['cargo'];
    }
    if (item?.ehGerente || item?.eh_gerente) return 'GERENTE';
    if (item?.ehSocio || item?.eh_socio_proprietario) return 'SOCIO';
    return 'CORRETOR';
  };

  const formatCargoLabel = (cargo?: Broker['cargo'], hasAgency?: boolean) => {
    switch (cargo) {
      case 'SOCIO':
        return 'Socio';
      case 'GERENTE':
        return 'Gerente';
      case 'CLIENTE':
        return 'Cliente';
      default:
        return hasAgency === false ? 'Autônomo' : 'Corretor';
    }
  };

  const getAgencyWarningLabel = (broker?: Broker | null) => {
    if (!broker) return null;
    const status = (broker.agencyStatus || '').toUpperCase();
    if (status === 'SUSPENSA') return 'Imobiliaria suspensa';
    if (status === 'REJEITADA') return 'Imobiliaria rejeitada';
    return null;
  };

  const normalizeTag = (value: string) => value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  const normalizeTagKey = (value: string) => normalizeTag(value).toLowerCase();

  const parsePracaIds = (value: unknown): number[] | undefined => {
    if (Array.isArray(value)) {
      const parsed = value
        .map(item => Number(item))
        .filter(item => Number.isFinite(item));
      return parsed.length ? parsed : undefined;
    }
    if (typeof value === 'string') {
      const parsed = value
        .split(',')
        .map(item => Number(item.trim()))
        .filter(item => Number.isFinite(item));
      return parsed.length ? parsed : undefined;
    }
    return undefined;
  };

  const tagSuggestions = useMemo(() => {
    const query = normalizeTag(tagInput);
    if (!query) return [];
    const normalized = normalizeTagKey(query);
    const current = new Set(brokerTags.map(tag => normalizeTagKey(tag.name)));
    return availableTags
      .filter(tag => {
        const key = normalizeTagKey(tag.name);
        return key.includes(normalized) && !current.has(key);
      })
      .slice(0, 6);
  }, [tagInput, availableTags, brokerTags]);

  const isTagsDirty = useMemo(() => {
    const current = brokerTags.map(tag => normalizeTagKey(tag.name)).sort();
    const snapshot = [...tagSnapshotRef.current].sort();
    if (current.length !== snapshot.length) return true;
    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== snapshot[i]) return true;
    }
    return false;
  }, [brokerTags]);

  const renderAgencyLabel = (broker: Broker) => {
    const agencyId = broker.agencyId
      ?? agenciesList.find(agency => agency.name.toLowerCase() === broker.agency.toLowerCase())?.id;
    const label = broker.agency || 'Sem imobiliaria';
    if (!agencyId) {
      return broker.cargo === 'CLIENTE' ? 'Cliente' : 'Autônomo';
    }
    return (
      <Link
        to={`/imobiliarias/${agencyId}/visao-geral`}
        className="hover:underline"
        title="Ver pagina da imobiliaria"
      >
        {label}
      </Link>
    );
  };

  const mapBroker = (item: any): Broker => {
    const cargo = resolveCargo(item);
    const hasAgency = Boolean(item.imobiliariaId ?? item.imobiliaria_id ?? item.imobiliariaNome);
    const fallbackAgency = cargo === 'CLIENTE' ? 'Cliente' : 'Autônomo';
    return {
      id: item.id,
      name: item.nome || 'Sem nome',
      agency: item.imobiliariaNome || (hasAgency ? 'Sem imobiliaria' : fallbackAgency),
      agencyId: item.imobiliariaId ?? item.imobiliaria_id ?? null,
      phone: item.telefone || '',
      email: item.email || '',
      creci: item.creci || '',
      cargo,
      agencyStatus: item.imobiliariaStatus ?? item.imobiliaria_status ?? null,
      agencyActive: item.imobiliariaAtiva === undefined && item.imobiliaria_ativa === undefined
        ? null
        : !!(item.imobiliariaAtiva ?? item.imobiliaria_ativa),
      status: item.ativo ? 'active' : 'blocked',
      bio: item.bio || null,
      fotoUrl: normalizePhotoUrl(item.fotoUrl || item.foto_url),
      possuiWhatsapp: item.possuiWhatsapp ?? item.possui_whatsapp ?? null,
      cvcrmId: item.cvcrmId ?? item.cvcrm_id ?? null,
    };
  };

  // Fetch Brokers (list)
  useEffect(() => {
    if (viewMode !== 'list') return;
    const controller = new AbortController();
    const fetchBrokers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(pageSize));
        if (search) params.set('q', search);
        const response = await fetch(`${CORE_API}/corretores?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Falha ao carregar corretores');
        const data = await response.json();
        const mapped: Broker[] = (data?.corretores || []).map(mapBroker);
        const total = Number(data?.total ?? mapped.length);
        setBrokers(mapped);
        setTotalCount(total);
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        if (page > totalPages) {
          setPage(totalPages);
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao buscar corretores', err);
        setError('Nao foi possivel carregar os corretores agora.');
        setBrokers([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrokers();
    return () => controller.abort();
  }, [viewMode, search, page, pageSize]);

  // Fetch Agencies List
  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const response = await fetch(`${CORE_API}/imobiliarias`, { headers: { ...getAuthHeaders() } });
        if (response.ok) {
          const data = await response.json();
          const list = (data?.imobiliarias || []).map((item: any) => ({
            id: item.id,
            name: item.nomeFantasia || item.nome_fantasia || item.nome || item.razaoSocial || "Imobiliaria",
            pracaId: item.pracaId ?? item.praca_id ?? item.praca?.id ?? null,
            pracaNome: item.pracaNome ?? item.praca_nome ?? item.praca?.nome ?? null,
            pracaIds: parsePracaIds(item.pracaIds ?? item.praca_ids),
          }));
          setAgenciesList(list);
        }
      } catch (error) {
        console.error("Erro ao buscar imobiliarias:", error);
      }
    };
    fetchAgencies();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchPracas = async () => {
      setPracasLoading(true);
      try {
        const response = await fetch(`${CORE_API}/pracas`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Falha ao carregar pracas');
        const data = await response.json();
        const list = (data?.pracas || data || []).map((item: any) => ({
          id: item.id,
          nome: item.nome || item.name || 'Praca',
        }));
        setPracas(Array.isArray(list) ? list : []);
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        console.error('Erro ao buscar pracas:', error);
        setPracas([]);
      } finally {
        setPracasLoading(false);
      }
    };
    fetchPracas();
    return () => controller.abort();
  }, []);

  // Auto-select broker if coming from support page
  useEffect(() => {
    if (!initialBrokerId) {
      setViewMode('list');
      setSelectedBroker(null);
      setProfileTab('ficha');
      return;
    }
    setViewMode('detail');
    setSelectedBroker(null);
    setProfileTab('ficha');

    const fetchBroker = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(`${CORE_API}/corretores/${initialBrokerId}`, { headers: { ...getAuthHeaders() } });
        if (!res.ok) throw new Error('Falha ao carregar corretor');
        const data = await res.json();
        if (data?.corretor) {
          setSelectedBroker(mapBroker(data.corretor));
        } else {
          setSelectedBroker(null);
        }
      } catch (err) {
        console.error('Erro ao buscar corretor', err);
        setError('Nao foi possivel carregar o corretor agora.');
        setSelectedBroker(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBroker();
  }, [initialBrokerId]);

  useEffect(() => {
    if (!selectedBroker) {
      setBrokerKpis(null);
      return;
    }
    const controller = new AbortController();
    const fetchBrokerKpis = async () => {
      setBrokerKpisLoading(true);
      try {
        const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}/kpis`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('Falha ao carregar KPIs do corretor');
        const data = await res.json();
        setBrokerKpis(data || null);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao carregar KPIs do corretor', err);
        setBrokerKpis(null);
      } finally {
        setBrokerKpisLoading(false);
      }
    };
    fetchBrokerKpis();
    return () => controller.abort();
  }, [selectedBroker?.id]);

  useEffect(() => {
    if (profileTab !== 'conversa' || !selectedBroker) return;
    setChatMessages([]);
    setChatCursor(null);
    setChatHasMore(false);
    setChatShowLoadMore(false);
    chatAtendimentoIdsRef.current = new Set();
    chatInitialLoadRef.current = true;
    fetchChatMessages({ reset: true });
  }, [profileTab, selectedBroker?.id]);

  useEffect(() => {
    if (profileTab !== 'conversa') return;
    setConversaTab('conversa');
  }, [profileTab, selectedBroker?.id]);

  useEffect(() => {
    if (profileTab !== 'conversa' || conversaTab !== 'atendimentos') return;
    setBrokerTicketsPage(1);
  }, [profileTab, conversaTab, selectedBroker?.id]);

  useEffect(() => {
    if (profileTab !== 'conversa' || conversaTab !== 'atendimentos' || !selectedBroker) return;
    const controller = new AbortController();
    const fetchBrokerTickets = async () => {
      setBrokerTicketsLoading(true);
      setBrokerTicketsError(null);
      try {
        const fetchLimit = BROKER_TICKETS_PAGE_SIZE + 1;
        const offset = (brokerTicketsPage - 1) * BROKER_TICKETS_PAGE_SIZE;
        const params = new URLSearchParams({
          limit: String(fetchLimit),
          offset: String(offset),
          statuses: 'aguardando_atendimento,em_atendimento,aguardando_resposta_corretor,encerrado,descartado',
          corretorId: String(selectedBroker.id)
        });
        const res = await fetch(`${CORE_API}/atendimentos/list?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal
        });
        if (!res.ok) throw new Error('Falha ao carregar atendimentos');
        const data = await res.json();
        const received = data?.atendimentos || [];
        setBrokerTickets(received.slice(0, BROKER_TICKETS_PAGE_SIZE));
        setBrokerTicketsHasMore(received.length > BROKER_TICKETS_PAGE_SIZE);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao carregar atendimentos do corretor', err);
        setBrokerTickets([]);
        setBrokerTicketsError('Nao foi possivel carregar os atendimentos do corretor.');
      } finally {
        setBrokerTicketsLoading(false);
      }
    };
    fetchBrokerTickets();
    return () => controller.abort();
  }, [profileTab, conversaTab, selectedBroker?.id, brokerTicketsPage]);

  const fetchActiveAtendimento = async () => {
    if (!selectedBroker) return;
    setActiveAtendimentoLoading(true);
    setActiveAtendimentoError(null);
    setActiveAtendimentoId(null);
    setActiveAtendimentoStatus(null);
    try {
      const params = new URLSearchParams({
        limit: '1',
        offset: '0',
        statuses: 'aguardando_atendimento,em_atendimento,aguardando_resposta_corretor',
        corretorId: String(selectedBroker.id)
      });
      const res = await fetch(`${CORE_API}/atendimentos/list?${params.toString()}`, {
        headers: { ...getAuthHeaders() }
      });
      if (!res.ok) throw new Error('Falha ao carregar atendimento ativo');
      const data = await res.json();
      const first = (data?.atendimentos || [])[0];
      setActiveAtendimentoId(first?.id ?? null);
      setActiveAtendimentoStatus(first?.status ?? null);
      if (typeof first?.id === 'number') {
        chatAtendimentoIdsRef.current.add(first.id);
      }
    } catch (err) {
      console.error('Erro ao buscar atendimento ativo', err);
      setActiveAtendimentoId(null);
      setActiveAtendimentoStatus(null);
      setActiveAtendimentoError('Nao foi possivel validar o atendimento ativo.');
    } finally {
      setActiveAtendimentoLoading(false);
    }
  };

  const fetchCloseCategories = async () => {
    try {
      const res = await fetch(`${CORE_API}/atendimentos/categorias`, { headers: { ...getAuthHeaders() } });
      if (!res.ok) return;
      const json = await res.json();
      setCloseCategories(json?.categorias || []);
    } catch (err) {
      console.error('Erro ao carregar categorias de atendimento', err);
    }
  };

  const fetchCloseProducts = async (imobiliariaId?: number | null) => {
    if (!imobiliariaId) {
      setCloseProducts([]);
      return;
    }
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${imobiliariaId}/produtos`, { headers: { ...getAuthHeaders() } });
      if (!res.ok) return;
      const json = await res.json();
      const list = (json?.produtos || []).map((p: any) => ({ id: p.id, nome: p.nome || `Produto ${p.id}` }));
      setCloseProducts(list);
      if (!closeData.productId && list.length > 0) {
        setCloseData(prev => ({ ...prev, productId: String(list[0].id) }));
      }
    } catch (err) {
      console.error('Erro ao carregar produtos da imobiliaria', err);
    }
  };

  const loadBrokerAuditLogs = async (
    corretorId: number,
    options?: { reset?: boolean; signal?: AbortSignal }
  ) => {
    const reset = !!options?.reset;
    const offset = reset ? 0 : brokerAuditLogsOffset;
    if (reset) {
      setBrokerAuditLogsLoading(true);
      setBrokerAuditLogsError(null);
      setBrokerAuditLogsHasMore(true);
    } else {
      setBrokerAuditLogsLoadingMore(true);
    }
    try {
      const res = await fetch(
        `${CORE_API}/corretores/${corretorId}/logs?limit=${brokerAuditLogsPageSize}&offset=${offset}`,
        {
          headers: { ...getAuthHeaders() },
          signal: options?.signal,
        }
      );
      if (!res.ok) throw new Error('Falha ao carregar logs');
      const data = await res.json();
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      setBrokerAuditLogs(prev => (reset ? logs : [...prev, ...logs]));
      setBrokerAuditLogsHasMore(logs.length === brokerAuditLogsPageSize);
      setBrokerAuditLogsOffset(offset + logs.length);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      if (reset) setBrokerAuditLogs([]);
      setBrokerAuditLogsError('Nao foi possivel carregar a linha do tempo.');
    } finally {
      if (reset) {
        setBrokerAuditLogsLoading(false);
      } else {
        setBrokerAuditLogsLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedBroker?.id) return;
    const controller = new AbortController();
    loadBrokerAuditLogs(selectedBroker.id, { reset: true, signal: controller.signal });
    return () => controller.abort();
  }, [selectedBroker?.id]);

  const handleBrokerAuditScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (brokerAuditLogsLoading || brokerAuditLogsLoadingMore || !brokerAuditLogsHasMore) return;
    if (!selectedBroker?.id) return;
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 120) {
      loadBrokerAuditLogs(selectedBroker.id);
    }
  };

  const loadBrokerTags = async (corretorId: number) => {
    setTagError(null);
    setTagSuccess(null);
    try {
      const [tagsRes, brokerTagsRes] = await Promise.all([
        fetch(`${CORE_API}/corretores/tags`, { headers: { ...getAuthHeaders() } }),
        fetch(`${CORE_API}/corretores/${corretorId}/tags`, { headers: { ...getAuthHeaders() } }),
      ]);
      const tagsPayload = tagsRes.ok ? await tagsRes.json() : {};
      const brokerPayload = brokerTagsRes.ok ? await brokerTagsRes.json() : {};
      const nextAvailable = Array.isArray(tagsPayload?.tags) ? tagsPayload.tags : [];
      const nextBroker = Array.isArray(brokerPayload?.tags) ? brokerPayload.tags : [];
      setAvailableTags(nextAvailable);
      setBrokerTags(nextBroker);
      tagSnapshotRef.current = nextBroker.map((tag: TagOption) => normalizeTagKey(tag.name));
    } catch (err) {
      console.error('Erro ao carregar tags do corretor', err);
      setAvailableTags([]);
      setBrokerTags([]);
      tagSnapshotRef.current = [];
    }
  };

  useEffect(() => {
    if (!selectedBroker?.id) return;
    loadBrokerTags(selectedBroker.id);
  }, [selectedBroker?.id]);

  useEffect(() => {
    if (profileTab !== 'conversa' || !selectedBroker) return;
    fetchActiveAtendimento();
  }, [profileTab, selectedBroker?.id]);

  useEffect(() => {
    if (!closeModalOpen) return;
    if (closeCategories.length === 0) {
      fetchCloseCategories();
    }
    fetchCloseProducts(selectedBroker?.agencyId ?? null);
  }, [closeModalOpen, selectedBroker?.agencyId]);

  const markPhotoAsBroken = (brokerId: number) => {
    setBrokenPhotoIds(prev => {
      if (prev.has(brokerId)) return prev;
      const next = new Set(prev);
      next.add(brokerId);
      return next;
    });
  };

  const shouldIgnoreTicketMessage = (item: any) => {
    const body = (item?.body || '').trim();
    const mediaType = String(item?.media_type || '').toUpperCase();
    const hasMedia = !!item?.media_url;
    return !body && !hasMedia && mediaType === 'OTHER';
  };

  const mapBrokerMessage = (item: any): BrokerMessage => ({
    id: item.id,
    body: item.body || '',
    direction: item.direction === 'OUT' ? 'OUT' : item.direction === 'SYS' ? 'SYS' : 'IN',
    createdAt: item.sent_at || item.created_at || new Date().toISOString(),
    mediaType: item.media_type || null,
    mediaUrl: item.media_url || null,
    atendimentoId: item.atendimento_id || null,
    whatsappMsgId: item.whatsapp_msg_id || null,
  });

  const formatTime = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${day} ${time}`;
  };

  const formatTimelineDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const getAuditResponsavelLabel = (responsavel?: { nome?: string; email?: string; tipo?: string }) => {
    if (!responsavel) return '';
    if (responsavel.nome) return responsavel.nome;
    if (responsavel.email) return responsavel.email;
    if (responsavel.tipo) return responsavel.tipo;
    return '';
  };

  const formatBrokerAuditValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    if (key === 'ativo') return value ? 'Ativo' : 'Inativo';
    if (key === 'telefone') return maskPhone(String(value));
    if (key === 'imobiliariaId') {
      const numericId = Number(value);
      if (!Number.isFinite(numericId) || numericId <= 0) return '-';
      const agency = agenciesList.find(item => item.id === numericId);
      return agency ? `${agency.name} (#${numericId})` : `Imobiliaria #${numericId}`;
    }
    if (key === 'ehSocio') return value ? 'Sim' : 'Nao';
    if (key === 'ehGerente') return value ? 'Sim' : 'Nao';
    return String(value);
  };

  const getBrokerAuditFieldLabel = (key: string) => {
    const labels: Record<string, string> = {
      nome: 'Nome',
      email: 'Email',
      telefone: 'Celular',
      creci: 'CRECI',
      bio: 'Bio',
      imobiliariaId: 'Imobiliaria',
      ativo: 'Status',
      ehSocio: 'Socio',
      ehGerente: 'Gerente'
    };
    return labels[key] || key;
  };

  const describeBrokerAuditEvent = (entry: AuditLogEntry) => {
    const event = entry.event || entry.data?.event || '';
    const changes = entry.data?.changes || {};
    const previous = entry.data?.previous || {};
    const responsavelLabel = getAuditResponsavelLabel(entry.data?.responsavel);
    const responsavelSuffix = responsavelLabel ? ` por ${responsavelLabel}` : '';
    const responsavelDetail = responsavelSuffix ? `Alteracao${responsavelSuffix}.` : '';
    const withResponsavelDetail = (detail: string, detailLines?: string[]) => {
      if (!responsavelDetail) {
        return { detail, detailLines, detailStrong: false };
      }
      return {
        detail: responsavelDetail,
        detailLines: detailLines?.length ? detailLines : [detail],
        detailStrong: true,
      };
    };

    switch (event) {
      case 'corretor.create':
        return {
          title: 'Cadastro criado',
          ...withResponsavelDetail('Corretor registrado no sistema.'),
        };
      case 'corretor.update': {
        if (changes && Object.keys(changes).length) {
          const lines = Object.keys(changes).map((key) => {
            const label = getBrokerAuditFieldLabel(key);
            const fromValue = formatBrokerAuditValue(key, previous[key]);
            const toValue = formatBrokerAuditValue(key, changes[key]);
            return `${label}: ${fromValue} -> ${toValue}`;
          });
          return {
            title: 'Dados atualizados',
            ...withResponsavelDetail('Informacoes atualizadas.', lines),
          };
        }
        return {
          title: 'Dados atualizados',
          detail: 'Atualizacao registrada.',
        };
      }
      default:
        return {
          title: event || 'Evento registrado',
          detail: 'Atualizacao registrada.',
        };
    }
  };

  const renderBrokerTimelinePanel = () => (
    <div className={`p-4 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-base flex items-center gap-2">
          <FileText size={18} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
          Historico
        </h3>
        {brokerAuditLogsLoading && (
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</span>
        )}
      </div>

      <div className="pr-2" onScroll={handleBrokerAuditScroll}>
        {brokerAuditLogsError && (
          <div className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{brokerAuditLogsError}</div>
        )}

        {!brokerAuditLogsLoading && !brokerAuditLogsError && brokerAuditLogs.length === 0 && (
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nenhum evento registrado.</div>
        )}

        {!brokerAuditLogsLoading && !brokerAuditLogsError && brokerAuditLogs.length > 0 && (
          <div className="space-y-4">
            {brokerAuditLogs.map((entry, index) => {
              const { title, detail, detailLines, detailStrong } = describeBrokerAuditEvent(entry) as {
                title: string;
                detail: string;
                detailLines?: string[];
                detailStrong?: boolean;
              };
              return (
                <div key={entry.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full ${darkMode ? 'bg-vision-activeRed' : 'bg-[#294b29]'}`} />
                    {index !== brokerAuditLogs.length - 1 && (
                      <div className={`w-px flex-1 ${darkMode ? 'bg-vision-border' : 'bg-gray-200'}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm">{title}</p>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatTimelineDate(entry.createdAt)}
                      </span>
                    </div>
                    {detailStrong ? (
                      <p className={`text-xs mt-1 font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {detail}
                      </p>
                    ) : (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{detail}</p>
                    )}
                    {detailLines && detailLines.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {detailLines.map((line) => (
                          <p key={line} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {brokerAuditLogsLoadingMore && (
          <div className={`text-xs mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</div>
        )}
      </div>
    </div>
  );

  const normalizeMediaUrl = (raw?: string | null, token?: string): string | undefined => {
    if (!raw) return undefined;
    const base = CORE_API.replace(/\/$/, '');
    const hasProtocol = /^https?:\/\//i.test(raw);
    const cleanedPath = raw.replace(/^\/+/, '');
    const url = hasProtocol ? raw : `${base}/${cleanedPath}`;
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(token)}`;
  };

  const parseContact = (text: string) => {
    const normalizeDigits = (value: string) => value.replace(/\D/g, '');
    const vcardName = text.match(/^FN:(.+)$/m)?.[1]?.trim();
    const telRaw = text.match(/^(?:\\w+\\.)?TEL[^:]*:(.+)$/m)?.[1]?.trim();
    const tel = telRaw ? normalizeDigits(telRaw) : '';
    if (vcardName || tel) return { name: vcardName || 'Contato', phone: tel || '' };
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length >= 2) return { name: lines[0], phone: normalizeDigits(lines[1]) || lines[1] };
    if (lines.length === 1) return { name: lines[0], phone: '' };
    return { name: 'Contato', phone: '' };
  };

  const renderTicketMessageContent = (msg: BrokerMessage) => {
    const mediaType = (msg.mediaType || '').toUpperCase();
    const mediaSrc = normalizeMediaUrl(msg.mediaUrl || undefined, getAuthToken());
    const mapMatch = msg.body ? msg.body.match(/https?:\/\/www\.google\.com\/maps\?q=[^\s]+/i) : null;
    const mapUrl = mapMatch ? mapMatch[0] : null;

    if (mediaSrc && (mediaType === 'IMAGE' || mediaType === 'STICKER')) {
      return (
        <div className="space-y-1">
          <img
            src={mediaSrc}
            alt={msg.body || (mediaType === 'STICKER' ? 'Figurinha' : 'Imagem')}
            className="max-w-full md:max-w-xs rounded-lg border border-black/5"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          {msg.body ? renderChatBody(msg.body) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'VIDEO') {
      return (
        <div className="space-y-1">
          <video
            controls
            className="w-full max-w-xs md:max-w-sm rounded-lg"
            style={{ maxHeight: 260 }}
            src={mediaSrc}
          />
          {msg.body ? renderChatBody(msg.body) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'AUDIO') {
      return (
        <div className="space-y-1">
          <audio controls className="w-64 max-w-full" src={mediaSrc} style={{ minWidth: 180 }} />
          {msg.body ? renderChatBody(msg.body) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'DOCUMENT') {
      return (
        <div className="space-y-1">
          <a
            href={mediaSrc}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            Baixar arquivo
          </a>
          {msg.body ? renderChatBody(msg.body) : null}
        </div>
      );
    }

    if (mapUrl || mediaType === 'LOCATION') {
      const prefix = (msg.body || '');
      const finalPrefix = mapUrl ? prefix.replace(mapUrl, '').trim() : prefix.trim();
      return (
        <div className="space-y-1">
          {finalPrefix ? renderChatBody(finalPrefix) : null}
          {mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              Ver localizacao no Google Maps
            </a>
          ) : (
            <span className="text-sm text-gray-500">Localizacao recebida.</span>
          )}
        </div>
      );
    }

    if (mediaType === 'CONTACT') {
      const contact = parseContact(msg.body || '');
      return (
        <div className={`rounded-lg border px-3 py-2 ${darkMode ? 'border-vision-border bg-black/30' : 'border-gray-200 bg-gray-50'}`}>
          <div className="text-xs uppercase tracking-wide text-gray-500">Contato recebido</div>
          <div className="text-sm font-semibold">{contact.name || 'Contato'}</div>
          {contact.phone ? <div className="text-sm text-gray-600">{contact.phone}</div> : null}
        </div>
      );
    }

    return renderChatBody(msg.body || '(mensagem)');
  };

  const renderBrokerChatContent = (msg: BrokerMessage, compact?: boolean) => {
    const mediaType = (msg.mediaType || '').toUpperCase();
    const mediaSrc = normalizeMediaUrl(msg.mediaUrl || undefined, getAuthToken());
    const mapMatch = msg.body ? msg.body.match(/https?:\/\/www\.google\.com\/maps\?q=[^\s]+/i) : null;
    const mapUrl = mapMatch ? mapMatch[0] : null;

    if (mediaSrc && (mediaType === 'IMAGE' || mediaType === 'STICKER')) {
      return (
        <div className="space-y-1">
          <img
            src={mediaSrc}
            alt={msg.body || (mediaType === 'STICKER' ? 'Figurinha' : 'Imagem')}
            className="max-w-full md:max-w-xs rounded-lg border border-black/5"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          {msg.body ? renderChatBody(msg.body, compact ? 'text-xs' : undefined) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'VIDEO') {
      return (
        <div className="space-y-1">
          <video
            controls
            className="w-full max-w-xs md:max-w-sm rounded-lg"
            style={{ maxHeight: 260 }}
            src={mediaSrc}
          />
          {msg.body ? renderChatBody(msg.body, compact ? 'text-xs' : undefined) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'AUDIO') {
      return (
        <div className="space-y-1">
          <audio controls className="w-64 max-w-full" src={mediaSrc} style={{ minWidth: 180 }} />
          {msg.body ? renderChatBody(msg.body, compact ? 'text-xs' : undefined) : null}
        </div>
      );
    }

    if (mediaSrc && mediaType === 'DOCUMENT') {
      return (
        <div className="space-y-1">
          <a
            href={mediaSrc}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            Baixar arquivo
          </a>
          {msg.body ? renderChatBody(msg.body, compact ? 'text-xs' : undefined) : null}
        </div>
      );
    }

    if (mapUrl || mediaType === 'LOCATION') {
      const prefix = (msg.body || '');
      const finalPrefix = mapUrl ? prefix.replace(mapUrl, '').trim() : prefix.trim();
      return (
        <div className="space-y-1">
          {finalPrefix ? renderChatBody(finalPrefix, compact ? 'text-xs' : undefined) : null}
          {mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              Ver localizacao no Google Maps
            </a>
          ) : (
            <span className="text-sm text-gray-500">Localizacao recebida.</span>
          )}
        </div>
      );
    }

    if (mediaType === 'CONTACT') {
      const contact = parseContact(msg.body || '');
      return (
        <div className={`rounded-lg border px-3 py-2 ${darkMode ? 'border-vision-border bg-black/30' : 'border-gray-200 bg-gray-50'}`}>
          <div className="text-xs uppercase tracking-wide text-gray-500">Contato recebido</div>
          <div className="text-sm font-semibold">{contact.name || 'Contato'}</div>
          {contact.phone ? <div className="text-sm text-gray-600">{contact.phone}</div> : null}
        </div>
      );
    }

    return renderChatBody(msg.body || '(mensagem)', compact ? 'text-xs' : undefined);
  };

  const getTicketStatusLabel = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'aguardando_atendimento':
        return 'Aguardando atendimento';
      case 'em_atendimento':
        return 'Em atendimento';
      case 'aguardando_resposta_corretor':
        return 'Aguardando corretor';
      case 'encerrado':
        return 'Encerrado';
      case 'descartado':
        return 'Descartado';
      default:
        return status || 'Indefinido';
    }
  };

  const getTicketStatusClasses = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'aguardando_atendimento':
        return darkMode ? 'bg-yellow-900/40 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'em_atendimento':
        return darkMode ? 'bg-blue-900/40 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'aguardando_resposta_corretor':
        return darkMode ? 'bg-orange-900/40 text-orange-200' : 'bg-orange-100 text-orange-800';
      case 'encerrado':
        return darkMode ? 'bg-green-900/40 text-green-200' : 'bg-green-100 text-green-800';
      case 'descartado':
        return darkMode ? 'bg-red-900/40 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-vision-surface text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };

  const renderBoldText = (text: string) => {
    const boldRegex = /\*([^*]+)\*/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      parts.push(<strong key={`bold-${match.index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
    }
    return parts.length ? parts : text;
  };

  const renderChatBody = (body: string, className?: string) => {
    if (!body) return <p className={`text-sm whitespace-pre-wrap break-all ${className || ''}`.trim()} />;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = body.split(urlRegex);
    return (
      <p className={`text-sm whitespace-pre-wrap break-all ${className || ''}`.trim()}>
        {parts.map((part, idx) => {
          if (urlRegex.test(part)) {
            return (
              <a
                key={`${part}-${idx}`}
                href={part}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 underline break-all"
              >
                {part}
              </a>
            );
          }
          return (
            <span key={`${idx}-text`}>
              {renderBoldText(part)}
            </span>
          );
        })}
      </p>
    );
  };

  const fetchTicketMessages = async (ticket: BrokerAtendimento) => {
    setTicketMessages([]);
    setTicketMessagesError(null);
    setTicketMessagesLoading(true);
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${ticket.id}/mensagens?limit=200`, {
        headers: { ...getAuthHeaders() }
      });
      if (!res.ok) throw new Error('Falha ao carregar mensagens');
      const json = await res.json();
      const incoming: BrokerMessage[] = (json.mensagens || [])
        .filter((item: any) => !shouldIgnoreTicketMessage(item))
        .map(mapBrokerMessage);
      setTicketMessages(incoming);
    } catch (err) {
      console.error('Erro ao carregar mensagens do atendimento', err);
      setTicketMessagesError('Nao foi possivel carregar as mensagens.');
    } finally {
      setTicketMessagesLoading(false);
    }
  };

  const openTicketModal = async (ticket: BrokerAtendimento) => {
    setSelectedTicket(ticket);
    setTicketModalOpen(true);
    await fetchTicketMessages(ticket);
  };

  const closeTicketModal = () => {
    setTicketModalOpen(false);
    setSelectedTicket(null);
    setTicketMessages([]);
    setTicketMessagesError(null);
  };

  const requestChatMessages = async (beforeId?: number | null, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    params.set('limit', String(CHAT_PAGE_SIZE));
    if (beforeId) params.set('beforeId', String(beforeId));
    const res = await fetch(`${CORE_API}/corretores/${selectedBroker?.id}/mensagens?${params.toString()}`, {
      headers: { ...getAuthHeaders() },
      signal
    });
    if (!res.ok) throw new Error('Falha ao carregar conversas');
    const json = await res.json();
    const incoming: BrokerMessage[] = (json.mensagens || [])
      .filter((item: any) => !shouldIgnoreTicketMessage(item))
      .map(mapBrokerMessage);
    const nextBefore = typeof json.nextBeforeId === 'number' ? json.nextBeforeId : null;
    return { incoming, nextBefore };
  };

  const fetchChatMessages = async (opts?: { reset?: boolean }) => {
    if (!selectedBroker) return;
    const reset = opts?.reset ?? false;
    let cursor = reset ? null : chatCursor;
    const controller = new AbortController();
    if (!reset && chatScrollRef.current) {
      chatPendingScrollRef.current = {
        height: chatScrollRef.current.scrollHeight,
        top: chatScrollRef.current.scrollTop
      };
    }

    setChatLoading(true);
    try {
      if (reset) {
        chatShouldScrollToBottomRef.current = true;
      }
      const { incoming, nextBefore } = await requestChatMessages(cursor, controller.signal);
      if (reset) {
        chatAtendimentoIdsRef.current = new Set();
        incoming.forEach((msg) => {
          if (typeof msg.atendimentoId === 'number') {
            chatAtendimentoIdsRef.current.add(msg.atendimentoId);
          }
        });
        setChatMessages(incoming);
      } else {
        incoming.forEach((msg) => {
          if (typeof msg.atendimentoId === 'number') {
            chatAtendimentoIdsRef.current.add(msg.atendimentoId);
          }
        });
        setChatMessages(prev => [...incoming, ...prev]);
      }
      setChatCursor(nextBefore);
      setChatHasMore(!!nextBefore);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Erro ao buscar conversas', err);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    if (!chatPendingScrollRef.current || !chatScrollRef.current) return;
    const { height, top } = chatPendingScrollRef.current;
    const nextHeight = chatScrollRef.current.scrollHeight;
    chatScrollRef.current.scrollTop = nextHeight - height + top;
    chatPendingScrollRef.current = null;
  }, [chatMessages.length]);

  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const nearTop = el.scrollTop <= 32;
    setChatShowLoadMore(nearTop);
  };

  useEffect(() => {
    if (!chatHasMore) {
      setChatShowLoadMore(false);
      return;
    }
    handleChatScroll();
  }, [chatMessages.length, chatHasMore]);

  useEffect(() => {
    if (!chatShouldScrollToBottomRef.current || !chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    chatShouldScrollToBottomRef.current = false;
  }, [chatMessages.length]);

  useEffect(() => {
    if (!chatInitialLoadRef.current || chatLoading || !chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    chatInitialLoadRef.current = false;
  }, [chatMessages.length, chatLoading]);

  useEffect(() => {
    if (profileTab !== 'conversa' || !selectedBroker) return;
    const token = typeof window !== 'undefined' ? (localStorage.getItem('va_token') || '') : '';
    if (!token) return;
    const coreBase = (CORE_API || '').replace(/\/$/, '');
    const url = `${coreBase}/atendimentos/events?token=${encodeURIComponent(token)}`;
    let active = true;

    const isNearBottom = (el: HTMLDivElement | null) => {
      if (!el) return true;
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      return distance <= 64;
    };

    const maybeAppendMessage = async (raw: any, direction: 'IN' | 'OUT') => {
      if (!active || !selectedBroker) return;
      const shouldIgnore =
        !String(raw?.body || '').trim() &&
        !raw?.mediaUrl &&
        String(raw?.mediaType || '').toUpperCase() === 'OTHER';
      if (shouldIgnore) return;
      const atendimentoId = typeof raw?.atendimentoId === 'number' ? raw.atendimentoId : Number(raw?.atendimentoId);
      if (!atendimentoId) return;

      let matchesBroker = chatAtendimentoIdsRef.current.has(atendimentoId);
      if (!matchesBroker) {
        try {
          const res = await fetch(`${CORE_API}/atendimentos/${atendimentoId}`, { headers: { ...getAuthHeaders() } });
          if (!res.ok) return;
          const json = await res.json();
          const corretorId = json?.atendimento?.id_corretor ?? json?.atendimento?.corretor_id ?? json?.atendimento?.idCorretor;
          if (corretorId === selectedBroker.id) {
            chatAtendimentoIdsRef.current.add(atendimentoId);
            matchesBroker = true;
          }
        } catch (err) {
          return;
        }
      }

      if (!matchesBroker) return;
      const shouldStickToBottom = direction === 'OUT' || isNearBottom(chatScrollRef.current);
      if (shouldStickToBottom) {
        chatShouldScrollToBottomRef.current = true;
      }

      setChatMessages(prev => {
        const id = raw?.mensagemId ?? raw?.id ?? Date.now();
        const exists = prev.some(m =>
          (raw?.mensagemId && m.id === raw.mensagemId) ||
          (raw?.whatsappMsgId && m.whatsappMsgId === raw.whatsappMsgId)
        );
        if (exists) return prev;
        const nextMsg: BrokerMessage = {
          id,
          body: raw?.body || (direction === 'IN' ? '(mensagem recebida)' : '(mensagem enviada)'),
          direction,
          createdAt: new Date().toISOString(),
          mediaType: raw?.mediaType || null,
          mediaUrl: raw?.mediaUrl || null,
          atendimentoId,
          whatsappMsgId: raw?.whatsappMsgId || null
        };
        return [...prev, nextMsg];
      });
    };

    const source = new EventSource(url);
    source.addEventListener('mensagem.recebida', ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || '{}');
        maybeAppendMessage(data, 'IN');
      } catch {
        /* ignore */
      }
    });
    source.addEventListener('mensagem.enviada', ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || '{}');
        maybeAppendMessage(data, 'OUT');
      } catch {
        /* ignore */
      }
    });

    return () => {
      active = false;
      source.close();
    };
  }, [profileTab, selectedBroker?.id]);

  const handleOpenAtendimento = async () => {
    if (!selectedBroker) return;
    setActiveAtendimentoLoading(true);
    setActiveAtendimentoError(null);
    try {
      const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}/atendimentos`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      if (!res.ok) throw new Error('Falha ao abrir chamado');
      const data = await res.json();
      const atendimentoId = data?.atendimentoId || data?.atendimento?.id || null;
      setActiveAtendimentoId(atendimentoId);
      setActiveAtendimentoStatus(data?.atendimento?.status || 'em_atendimento');
      if (typeof atendimentoId === 'number') {
        chatAtendimentoIdsRef.current.add(atendimentoId);
      }
      await fetchChatMessages({ reset: true });
      await fetchActiveAtendimento();
    } catch (err) {
      console.error('Erro ao abrir chamado', err);
      setActiveAtendimentoError('Nao foi possivel abrir o chamado agora.');
    } finally {
      setActiveAtendimentoLoading(false);
    }
  };

  const handleCloseAtendimento = async () => {
    if (!activeAtendimentoId) return;
    setCloseError(null);
    setCloseData({ productId: closeData.productId || '', category: '', reason: '' });
    setCloseModalOpen(true);
  };

  const ensureProdutoVinculado = async () => {
    if (!activeAtendimentoId || !selectedBroker) return;
    const produtoId = Number(closeData.productId);
    const imobiliariaId = selectedBroker.agencyId ? Number(selectedBroker.agencyId) : null;
    if (!produtoId || !imobiliariaId) return;
    await fetch(`${CORE_API}/atendimentos/${activeAtendimentoId}/vincular-corretor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({
        nome: selectedBroker.name || 'Corretor',
        email: selectedBroker.email || null,
        telefone: selectedBroker.phone || null,
        produtoId,
        imobiliariaId
      })
    });
  };

  const handleSubmitClose = async () => {
    if (!activeAtendimentoId) return;
    if (!closeData.productId) {
      setCloseError('Selecione um produto vinculado para encerrar.');
      return;
    }
    if (!closeData.category) {
      setCloseError('Selecione uma categoria para encerrar.');
      return;
    }
    setActiveAtendimentoClosing(true);
    setCloseSubmitting(true);
    setCloseError(null);
    try {
      await ensureProdutoVinculado();
      const res = await fetch(`${CORE_API}/atendimentos/${activeAtendimentoId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: 'encerrado', categoriaId: Number(closeData.category) })
      });
      if (!res.ok) {
        const message = await res.text().catch(() => '');
        throw new Error(message || 'Falha ao encerrar chamado');
      }
      setCloseModalOpen(false);
      setCloseData({ productId: '', category: '', reason: '' });
      await fetchChatMessages({ reset: true });
      await fetchActiveAtendimento();
      setActiveAtendimentoId(null);
      setActiveAtendimentoStatus(null);
    } catch (err) {
      console.error('Erro ao encerrar chamado', err);
      setCloseError('Nao foi possivel encerrar o chamado agora.');
    } finally {
      setCloseSubmitting(false);
      setActiveAtendimentoClosing(false);
    }
  };

  const fileToDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
    reader.readAsDataURL(file);
  });

  const handleChatFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (chatAttachment?.previewUrl) {
      URL.revokeObjectURL(chatAttachment.previewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setChatAttachment({ file, previewUrl, mime: file.type || '', name: file.name });
    setRecordingError(null);
  };

  const stopCurrentRecording = () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
  };

  const startRecording = async () => {
    setRecordingError(null);
    try {
      const oggMime = 'audio/ogg;codecs=opus';
      const useOgg = MediaRecorder.isTypeSupported(oggMime);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = useOgg ? new MediaRecorder(stream, { mimeType: oggMime }) : new MediaRecorder(stream);
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const mimeType = recorder.mimeType || (useOgg ? oggMime : 'audio/webm');
        const blob = new Blob(recordingChunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          setRecordingError('Nenhum audio capturado');
          return;
        }
        const ext = mimeType.includes('ogg') ? 'ogg' : (mimeType.includes('webm') ? 'webm' : 'audio');
        const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: mimeType });
        const previewUrl = URL.createObjectURL(file);
        setChatAttachment({ file, previewUrl, mime: file.type || mimeType, name: file.name });
        setIsRecording(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err: any) {
      setRecordingError(err?.message || 'Nao foi possivel acessar o microfone');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    stopCurrentRecording();
  };

  const handleSendChat = async () => {
    if (chatSending || chatUploading) return;
    if (!selectedBroker || (!chatInput.trim() && !chatAttachment)) return;
    if (!activeAtendimentoId) {
      setActiveAtendimentoError('Abra um chamado antes de conversar.');
      return;
    }
    const message = chatInput.trim();
    setActiveAtendimentoError(null);
    setChatSending(true);
    setChatUploading(!!chatAttachment);
    try {
      let mediaPayload: { mediaUrl: string; mediaType: string } | null = null;
      if (chatAttachment) {
        const dataUrl = await fileToDataUrl(chatAttachment.file);
        const uploadRes = await fetch(`${CORE_API}/atendimentos/${activeAtendimentoId}/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ dataUrl, fileName: chatAttachment.name })
        });
        if (!uploadRes.ok) {
          const errText = await uploadRes.text().catch(() => '');
          throw new Error(errText || 'Falha ao enviar midia');
        }
        const uploadJson = await uploadRes.json();
        mediaPayload = { mediaUrl: uploadJson.mediaUrl, mediaType: uploadJson.mediaType };
      }

      const payload: Record<string, any> = {};
      if (message) payload.message = message;
      if (mediaPayload) {
        payload.mediaUrl = mediaPayload.mediaUrl;
        payload.mediaType = mediaPayload.mediaType;
      }

      const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}/mensagens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Falha ao enviar mensagem');
      if (chatAttachment?.previewUrl) {
        URL.revokeObjectURL(chatAttachment.previewUrl);
      }
      setChatAttachment(null);
      setChatInput('');
      await fetchChatMessages({ reset: true });
    } catch (err) {
      console.error('Erro ao enviar mensagem', err);
    } finally {
      setChatSending(false);
      setChatUploading(false);
    }
  };

  // --- ACTIONS ---

  const handleOpenModal = (broker?: Broker) => {
    if (broker) {
      setFormData(broker);
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        creci: '',
        bio: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Don't clear selectedBroker if we are in detail view, just close modal
  };

  const handleOpenInfoModal = () => {
    if (!selectedBroker) return;
    const matchedAgency = agenciesList.find(agency => agency.id === selectedBroker.agencyId)
      ?? agenciesList.find(agency => agency.name.toLowerCase() === selectedBroker.agency.toLowerCase());
    setInfoForm({
      pracaId: matchedAgency?.pracaId ?? null,
      agencyId: selectedBroker.agencyId ?? null,
      creci: selectedBroker.creci || '',
      cargo: selectedBroker.cargo || 'CORRETOR',
      cvcrmId: selectedBroker.cvcrmId || '',
    });
    setInfoError(null);
    setInfoAgencySearch('');
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    setInfoError(null);
    setInfoAgencySearch('');
  };

  const handleOpenSyncModal = () => {
    setSyncOptions({ whatsapp: false, cvcrm: false });
    setSyncResult(null);
    setIsSyncModalOpen(true);
  };

  const handleRunSync = async () => {
    if (!selectedBroker) return;
    if (!syncOptions.whatsapp && !syncOptions.cvcrm) return;
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ whatsapp: syncOptions.whatsapp, cvcrm: syncOptions.cvcrm })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSyncResult({ error: json?.error || 'Nao foi possivel sincronizar agora.' });
        return;
      }
      if (json?.corretor) {
        const updated = mapBroker(json.corretor);
        setSelectedBroker(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
        setBrokers(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
      }
      setSyncResult(json || {});
    } catch (err) {
      console.error('Erro ao sincronizar corretor', err);
      setSyncResult({ error: 'Nao foi possivel sincronizar agora.' });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleAddTag = (value: string) => {
    const normalized = normalizeTag(value);
    if (!normalized) return;
    const key = normalizeTagKey(normalized);
    const exists = brokerTags.some((tag) => normalizeTagKey(tag.name) === key);
    if (exists) {
      setTagInput('');
      return;
    }
    const existingTag = availableTags.find((tag) => normalizeTagKey(tag.name) === key);
    const nextTag = existingTag || { id: Date.now(), name: normalized };
    setBrokerTags((prev) => [...prev, nextTag]);
    setTagInput('');
    setTagSuccess(null);
  };

  const handleRemoveTag = (value: string) => {
    const key = normalizeTagKey(value);
    setBrokerTags((prev) => prev.filter((tag) => normalizeTagKey(tag.name) !== key));
    setTagSuccess(null);
  };

  const handleSaveTags = async () => {
    if (!selectedBroker) return;
    setTagSaving(true);
    setTagError(null);
    setTagSuccess(null);
    try {
      const payload = { tags: brokerTags.map((tag) => tag.name) };
      const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Falha ao salvar tags');
      }
      const data = await res.json();
      const nextBrokerTags = Array.isArray(data?.tags) ? data.tags : brokerTags;
      setBrokerTags(nextBrokerTags);
      tagSnapshotRef.current = nextBrokerTags.map((tag: TagOption) => normalizeTagKey(tag.name));
      setTagSuccess('Tags salvas com sucesso.');
    } catch (err: any) {
      console.error('Erro ao salvar tags do corretor', err);
      setTagError(err?.message || 'Falha ao salvar tags');
    } finally {
      setTagSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    if (formData.id) {
      try {
        const payload = {
          nome: formData.name?.trim(),
          email: formData.email?.trim() || null,
          telefone: formData.phone?.trim() || null,
          creci: formData.creci || null,
          bio: formData.bio || null,
          cvcrmId: formData.cvcrmId || null,
        };
        const res = await fetch(`${CORE_API}/corretores/${formData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const message = await res.text().catch(() => '');
          setError(message || 'Nao foi possivel salvar o corretor.');
          return;
        }
        const json = await res.json();
        const updated = mapBroker(json?.corretor || formData);
        setBrokers(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
        if (selectedBroker && selectedBroker.id === updated.id) {
          setSelectedBroker({ ...selectedBroker, ...updated });
        }
        await loadBrokerAuditLogs(updated.id, { reset: true });
        setError(null);
      } catch (err) {
        console.error('Erro ao salvar corretor', err);
        setError('Nao foi possivel salvar o corretor.');
        return;
      }
    } else {
      // Add
      const newBroker: Broker = {
        id: Date.now(),
        name: formData.name || '',
        agency: formData.agency || '',
        agencyId: formData.agencyId || null,
        phone: formData.phone || '',
        email: formData.email || '',
        creci: formData.creci || '',
        status: (formData.status || 'active') as 'active' | 'blocked',
        bio: formData.bio || 'Novo corretor cadastrado.',
        fotoUrl: formData.fotoUrl || null,
        // Default gamification for new users
        gamificacao: {
          corretorId: 0,
          pontos: 0,
          nivel: 'BRANCA',
          streak: 0,
          ultimaAtividade: new Date().toISOString(),
          progressoProximoNivel: 0
        }
      };
      setBrokers([...brokers, newBroker]);
    }
    handleCloseModal();
  };

  const handleSaveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBroker) return;
    setInfoSaving(true);
    setInfoError(null);
    if (infoForm.pracaId && !infoForm.agencyId) {
      setInfoError('Selecione uma imobiliaria para salvar ou remova a vinculacao.');
      setInfoSaving(false);
      return;
    }
    try {
      const payload = {
        creci: infoForm.creci || null,
        cargo: infoForm.cargo || null,
        cvcrmId: infoForm.cvcrmId || null,
        imobiliariaId: infoForm.agencyId || null,
      };
      const res = await fetch(`${CORE_API}/corretores/${selectedBroker.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const message = await res.text().catch(() => '');
        setInfoError(message || 'Nao foi possivel salvar as informacoes.');
        return;
      }
      const json = await res.json();
      const agency = agenciesList.find(item => item.id === infoForm.agencyId);
      const updated = mapBroker(json?.corretor || {
        ...selectedBroker,
        creci: infoForm.creci,
        cargo: infoForm.cargo,
        cvcrmId: infoForm.cvcrmId || null,
        agency: infoForm.agencyId ? (agency?.name ?? selectedBroker.agency) : 'Autônomo',
        agencyId: infoForm.agencyId ?? null
      });
      setBrokers(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
      setSelectedBroker(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
      await loadBrokerAuditLogs(updated.id, { reset: true });
      setIsInfoModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar informacoes do corretor', err);
      setInfoError('Nao foi possivel salvar as informacoes.');
    } finally {
      setInfoSaving(false);
    }
  };

  const handleBlockBroker = (e: React.MouseEvent, id: number) => {
    e.stopPropagation(); // Prevent opening detail view
    const updatedBrokers = brokers.map(b => {
      if (b.id === id) {
        const newStatus = b.status === 'active' ? 'blocked' : 'active';
        // If blocking/unblocking the currently selected broker
        if (selectedBroker && selectedBroker.id === id) {
            setSelectedBroker({ ...selectedBroker, status: newStatus as 'active' | 'blocked' });
        }
        return { ...b, status: newStatus };
      }
      return b;
    });
    setBrokers(updatedBrokers as Broker[]);
  };

  const handleSelectBroker = (broker: Broker) => {
    navigate(`/corretores/${broker.id}`);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedBroker(null);
    navigate('/corretores');
  };

  // --- HELPERS ---
  const maskPhone = (v: string) => {
    v = v.replace(/\D/g, '');
    if (v.startsWith('55') && v.length > 11) v = v.slice(2);
    v = v.substring(0, 11);
    if (v.length > 10) return v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (v.length > 5) return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    if (v.length > 2) return v.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    return v;
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visibleBrokers = normalizedSearch
    ? brokers.filter(broker => (
      broker.name.toLowerCase().includes(normalizedSearch)
      || broker.agency.toLowerCase().includes(normalizedSearch)
      || broker.email.toLowerCase().includes(normalizedSearch)
      || broker.phone.toLowerCase().includes(normalizedSearch)
      || broker.creci.toLowerCase().includes(normalizedSearch)
    ))
    : brokers;

  const brokerModal = isModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`
        w-full max-w-md rounded-2xl shadow-2xl transform transition-all scale-100 flex flex-col max-h-[90vh]
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        <div className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-xl font-bold">
            {formData.id ? 'Editar Corretor' : 'Novo Corretor'}
          </h3>
          <button
            onClick={handleCloseModal}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="space-y-2">
            <label className="text-sm font-medium ml-1">Nome Completo</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`
                w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode
                  ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                  : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                }
              `}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium ml-1">CECI</label>
              <input
                type="text"
                value={formData.creci}
                onChange={(e) => setFormData({ ...formData, creci: e.target.value })}
                className={`
                  w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                  ${darkMode
                    ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                    : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                  }
                `}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium ml-1">Celular</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`
                  w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                  ${darkMode
                    ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                    : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                  }
                `}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium ml-1">E-mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
              className={`
                w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode
                  ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                  : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                }
              `}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium ml-1">ID CVCRM</label>
            <input
              type="text"
              value={formData.cvcrmId || ''}
              onChange={(e) => setFormData({ ...formData, cvcrmId: e.target.value })}
              className={`
                w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode
                  ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                  : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                }
              `}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium ml-1">Bio / Observacoes</label>
            <textarea
              value={formData.bio || ''}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className={`
                w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all min-h-[96px]
                ${darkMode
                  ? 'bg-vision-surface border-vision-border focus:ring-gray-600'
                  : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                }
              `}
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              className={`
                w-full py-3 rounded-lg font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-95
                ${darkMode ? 'bg-vision-activeRed hover:bg-red-700' : 'bg-[#294b29] hover:bg-[#1a301a]'}
              `}
            >
              <CheckCircle2 size={20} />
              <span>{formData.id ? 'Salvar Alteracoes' : 'Cadastrar Corretor'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderFichaPanel = (showHeader = true) => {
    const fichaContent = (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <p className={`text-xs uppercase font-bold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nome Completo</p>
            <p className="font-medium">{selectedBroker?.name}</p>
          </div>
          <div>
            <p className={`text-xs uppercase font-bold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CECI</p>
            <p className="font-medium">{selectedBroker?.creci || 'Nao informado'}</p>
          </div>
          <div>
            <p className={`text-xs uppercase font-bold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Celular</p>
            <p className="font-medium">{selectedBroker ? maskPhone(selectedBroker.phone) : ''}</p>
          </div>
          <div>
            <p className={`text-xs uppercase font-bold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Email</p>
            <p className="font-medium">{selectedBroker?.email || 'Nao informado'}</p>
          </div>
          <div>
            <p className={`text-xs uppercase font-bold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>ID CVCRM</p>
            <p className="font-medium">{selectedBroker?.cvcrmId || 'Nao informado'}</p>
          </div>
        </div>

        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-vision-surface/50 border-vision-border' : 'bg-gray-50 border-gray-100'}`}>
          <p className={`text-xs uppercase font-bold mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Bio / Observacoes</p>
          <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {selectedBroker?.bio || 'Nenhuma informacao adicional.'}
          </p>
        </div>
      </>
    );

    return (
      <>
        {showHeader ? (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <User className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} size={20} />
              Ficha do Corretor
            </h3>
          </div>
        ) : null}

        <div className={showHeader ? 'grid grid-cols-1 xl:grid-cols-2 gap-6' : 'space-y-6'}>
          <div>{fichaContent}</div>
          {showHeader ? renderBrokerTimelinePanel() : null}
        </div>
      </>
    );
  };

  const renderConversaPanel = () => (
    <div className="flex flex-col min-h-[360px] max-h-[420px] overflow-hidden">
      <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          {(['conversa', 'atendimentos'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setConversaTab(tab)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all
                ${conversaTab === tab
                  ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white')
                  : (darkMode ? 'bg-vision-darkGreen text-gray-400 hover:bg-vision-border' : 'bg-white text-gray-600 hover:bg-gray-100')
                }
              `}
            >
              {tab === 'conversa' ? 'Chat' : 'Chamados'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {activeAtendimentoLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : activeAtendimentoId ? (
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full font-semibold ${darkMode ? 'bg-vision-darkGreen text-gray-300' : 'bg-white text-gray-600 border border-gray-200'}`}>
                Chamado #{activeAtendimentoId}
              </span>
              {activeAtendimentoStatus ? (
                <span className={`px-2 py-1 rounded-full font-semibold ${getTicketStatusClasses(activeAtendimentoStatus)}`}>
                  {getTicketStatusLabel(activeAtendimentoStatus)}
                </span>
              ) : null}
              <button
                onClick={handleCloseAtendimento}
                disabled={activeAtendimentoClosing}
                className={`px-2.5 py-1 rounded-full font-semibold transition
                  ${activeAtendimentoClosing
                    ? (darkMode ? 'bg-vision-surface text-gray-600' : 'bg-gray-200 text-gray-400')
                    : (darkMode ? 'bg-red-900/50 text-red-300 hover:bg-red-900/70' : 'bg-red-50 text-red-600 hover:bg-red-100')
                  }
                `}
              >
                {activeAtendimentoClosing ? 'Encerrando...' : 'Encerrar'}
              </button>
            </div>
          ) : (
            <span className={`px-2 py-1 rounded-full font-semibold ${darkMode ? 'bg-vision-darkGreen text-gray-400' : 'bg-white text-gray-500 border border-gray-200'}`}>
              Sem chamado aberto
            </span>
          )}
        </div>
      </div>

      {conversaTab === 'conversa' ? (
        <>
          <div
            ref={chatScrollRef}
            onScroll={handleChatScroll}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 z-10 scrollbar-thin scrollbar-thumb-gray-600"
          >
            {chatLoading && chatMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center space-y-4">
                <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'}`} />
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando conversas...</span>
              </div>
            ) : (
              <>
                {chatHasMore && chatShowLoadMore && (
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={() => fetchChatMessages()}
                      className={`px-4 py-2 text-xs rounded-full shadow-sm font-semibold transition-all flex items-center gap-2
                        ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-gray-700/70' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                        ${chatLoading ? 'opacity-70 cursor-not-allowed' : ''}
                      `}
                      disabled={chatLoading}
                    >
                      {chatLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                      {chatLoading ? 'Carregando...' : 'Ver mensagens anteriores'}
                    </button>
                  </div>
                )}
                {chatMessages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500">
                    Nenhuma conversa registrada ainda. Use o campo abaixo para iniciar.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatMessages.map(msg => (
                      msg.direction === 'SYS' ? (
                        <div key={msg.id} className="flex justify-center my-2">
                          <div className={`text-xs px-4 py-2 rounded-lg break-all text-center max-w-sm ${darkMode ? 'bg-vision-darkGreen text-gray-400 border border-vision-border' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                            {renderBrokerChatContent(msg, true)}
                          </div>
                        </div>
                      ) : (
                        <div key={msg.id} className={`flex flex-col ${msg.direction === 'OUT' ? 'items-end' : 'items-start'}`}>
                          <div
                            className={`group relative max-w-[85%] md:max-w-[60%] px-3 py-2 text-sm sm:text-base rounded-2xl shadow-md transition-all duration-200
                              ${msg.direction === 'OUT'
                                ? darkMode
                                  ? 'bg-vision-activeRed text-white rounded-br-none'
                                  : 'bg-[#294b29] text-white rounded-br-none'
                                : darkMode
                                  ? 'bg-gray-700/70 text-gray-100 rounded-bl-none'
                                  : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                              }
                            `}
                          >
                            {renderBrokerChatContent(msg)}
                            <div className={`text-[10px] mt-1 flex justify-end ${msg.direction === 'OUT' ? 'text-white/60' : 'text-gray-400'}`}>
                              {formatTime(msg.createdAt)}
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {!activeAtendimentoId ? (
            <div className={`${darkMode ? "bg-black border-t border-vision-border" : "bg-white border-t border-gray-200"} p-6 text-center`}>
              <div className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Para conversar com este corretor, abra um chamado.
              </div>
              {activeAtendimentoError ? (
                <div className="text-xs text-red-400 mb-3">{activeAtendimentoError}</div>
              ) : null}
              <button
                onClick={handleOpenAtendimento}
                disabled={activeAtendimentoLoading}
                className={`px-5 py-2 rounded-lg font-bold text-white transition-all
                  ${activeAtendimentoLoading
                    ? (darkMode ? 'bg-vision-surface text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                    : (darkMode ? 'bg-[#294b29] hover:bg-green-600' : 'bg-[#294b29] hover:bg-green-600')
                  }
                `}
              >
                {activeAtendimentoLoading ? 'Abrindo...' : 'Abrir chamado'}
              </button>
            </div>
          ) : (
            <div className={`${darkMode ? "bg-black border-t border-vision-border" : "bg-white border-t border-gray-200"} px-4 pt-4 pb-2 sm:p-4`}>
              {chatAttachment && (
                <div className={`flex items-center gap-3 p-3 rounded-lg mb-3 ${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-gray-100 border border-gray-200"}`}>
                  {chatAttachment.mime.startsWith('image/') ? (
                    <img src={chatAttachment.previewUrl} alt={chatAttachment.name} className="w-12 h-12 rounded object-cover" />
                  ) : (
                    <Paperclip size={24} />
                  )}
                  <div className="flex-1 truncate text-sm">
                    {chatAttachment.name}
                  </div>
                  <button
                    type="button"
                    className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"}`}
                    onClick={() => {
                      URL.revokeObjectURL(chatAttachment.previewUrl);
                      setChatAttachment(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"}`}
                  title="Anexar imagem, video ou audio"
                  disabled={chatSending || chatUploading}
                >
                  <Paperclip size={22} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (isRecording) {
                      stopRecording();
                    } else {
                      startRecording();
                    }
                  }}
                  className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"} ${isRecording ? "text-red-500" : ""}`}
                  title={isRecording ? "Parar gravacao" : "Gravar audio"}
                  disabled={chatSending || chatUploading}
                >
                  <Mic size={22} />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Escreva uma mensagem..."
                    className={`w-full py-2.5 sm:py-3 px-4 rounded-2xl outline-none border focus:ring-2 transition-all text-sm sm:text-base
                      ${darkMode ? "bg-vision-darkGreen border-vision-border text-white focus:border-green-500 focus:ring-green-900/40" : "bg-gray-100 border-gray-300 text-gray-800 focus:border-[#294b29] focus:ring-[#294b29]/20"}`}
                    disabled={chatSending || chatUploading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSendChat();
                    }}
                  />
                </div>
                <button
                  onClick={handleSendChat}
                  disabled={chatSending || chatUploading || (!chatInput.trim() && !chatAttachment)}
                  className={`p-2 sm:p-3 rounded-full transition-all flex items-center justify-center shrink-0
                    ${chatSending || chatUploading || (!chatInput.trim() && !chatAttachment)
                      ? darkMode
                        ? "bg-vision-surface text-gray-600 cursor-not-allowed"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : darkMode
                        ? "bg-[#294b29] text-white hover:bg-green-600 shadow-md active:scale-95 transform hover:scale-110"
                        : "bg-[#294b29] text-white hover:bg-green-600 shadow-md active:scale-95 transform hover:scale-110"
                  }`}
                >
                  <Send size={20} />
                </button>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/*,audio/*"
                onChange={(e) => handleChatFiles(e.target.files)}
              />
              {chatUploading && (
                <div className="mt-2 text-xs text-gray-500">
                  Enviando midia...
                </div>
              )}
              {isRecording && (
                <div className="mt-2 text-xs text-red-500 flex items-center gap-2">
                  Gravando audio... <button type="button" onClick={stopRecording} className="px-2 py-1 bg-red-600 text-white rounded">Parar</button>
                </div>
              )}
              {recordingError && (
                <div className="mt-2 text-xs text-red-500">
                  {recordingError}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chamados do corretor</h4>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Historico e status dos atendimentos.</p>
            </div>
          </div>
          {brokerTicketsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando chamados...
            </div>
          ) : brokerTicketsError ? (
            <div className="text-sm text-red-500">{brokerTicketsError}</div>
          ) : brokerTickets.length === 0 ? (
            <div className="text-sm text-gray-500">Nenhum chamado encontrado para este corretor.</div>
          ) : (
            <div className="space-y-3">
              {brokerTickets.map(ticket => (
                <button
                  key={ticket.id}
                  onClick={() => openTicketModal(ticket)}
                  className={`w-full text-left p-4 rounded-xl border transition-all
                    ${darkMode ? 'bg-vision-darkGreen border-vision-border hover:bg-vision-surface' : 'bg-white border-gray-200 hover:bg-gray-50'}
                  `}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Chamado #{ticket.id}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {ticket.produto_nome || ticket.categoria_nome || 'Sem categoria'}
                      </div>
                    </div>
                    <span className={`text-[11px] px-2 py-1 rounded-full font-semibold ${getTicketStatusClasses(ticket.status)}`}>
                      {getTicketStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Atualizado: {formatDateTime(ticket.updated_at || ticket.created_at)}
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between text-xs pt-2">
            <button
              onClick={() => setBrokerTicketsPage(prev => Math.max(1, prev - 1))}
              disabled={brokerTicketsPage <= 1 || brokerTicketsLoading}
              className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} disabled:opacity-50`}
            >
              Anterior
            </button>
            <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Pagina {brokerTicketsPage}</span>
            <button
              onClick={() => setBrokerTicketsPage(prev => prev + 1)}
              disabled={!brokerTicketsHasMore || brokerTicketsLoading}
              className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} disabled:opacity-50`}
            >
              Proxima
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const fichaModal = isFichaModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-lg font-bold">Ficha do Corretor</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setIsFichaModalOpen(false);
                if (selectedBroker) handleOpenModal(selectedBroker);
              }}
              className={`h-8 w-8 rounded-full transition-all flex items-center justify-center
                ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
              title="Editar"
              aria-label="Editar"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => setIsFichaModalOpen(false)}
              className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
              aria-label="Fechar"
              title="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto">
          {renderFichaPanel(false)}
        </div>
      </div>
    </div>
  );

  const agenciesForPraca = infoForm.pracaId
    ? agenciesList.filter(agency => {
      if (Array.isArray(agency.pracaIds) && agency.pracaIds.length > 0) {
        return agency.pracaIds.includes(infoForm.pracaId as number);
      }
      return agency.pracaId === infoForm.pracaId;
    })
    : [];
  const normalizedAgencySearch = infoAgencySearch.trim().toLowerCase();
  const filteredAgenciesForPraca = normalizedAgencySearch
    ? agenciesForPraca.filter(agency => (
      agency.name.toLowerCase().includes(normalizedAgencySearch)
      || String(agency.id).includes(normalizedAgencySearch)
    ))
    : agenciesForPraca;

  const infoModal = isInfoModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-[90vh]
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-lg font-bold">Editar informacoes</h3>
          <button
            onClick={handleCloseInfoModal}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSaveInfo} className="p-5 space-y-4 overflow-y-auto">
          {infoError ? (
            <div className="text-sm text-red-500">{infoError}</div>
          ) : null}
          <div className="space-y-2">
            <label className="text-sm font-semibold">Praca</label>
            <select
              value={infoForm.pracaId ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setInfoForm(prev => ({ ...prev, pracaId: value, agencyId: null }));
                setInfoAgencySearch('');
              }}
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
              `}
              disabled={pracasLoading}
            >
              <option value="">{pracasLoading ? 'Carregando pracas...' : 'Selecione a praca'}</option>
              {pracas.map(praca => (
                <option key={praca.id} value={praca.id}>{praca.nome}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">Imobiliaria</label>
            <input
              type="text"
              value={infoAgencySearch}
              onChange={(e) => setInfoAgencySearch(e.target.value)}
              placeholder="Buscar imobiliaria"
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
              `}
            />
            <select
              value={infoForm.agencyId ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setInfoForm(prev => ({
                  ...prev,
                  agencyId: value,
                  pracaId: value ? prev.pracaId : null
                }));
                if (value) {
                  setInfoAgencySearch('');
                  setInfoError(null);
                }
              }}
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
              `}
              disabled={!infoForm.pracaId}
            >
              <option value="">{infoForm.pracaId ? 'Selecione a imobiliaria' : 'Selecione a praca primeiro'}</option>
              {filteredAgenciesForPraca.map(agency => (
                <option key={agency.id} value={agency.id}>{agency.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setInfoForm(prev => ({ ...prev, agencyId: null, pracaId: null }));
                setInfoAgencySearch('');
                setInfoError(null);
              }}
              className={`text-xs font-semibold underline ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Remover imobiliaria
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold">Cargo</label>
              <select
                value={infoForm.cargo || 'CORRETOR'}
                onChange={(e) => setInfoForm(prev => ({ ...prev, cargo: e.target.value as Broker['cargo'] }))}
                className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                  ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
                `}
              >
                <option value="CORRETOR">Corretor</option>
                <option value="GERENTE">Gerente</option>
                <option value="SOCIO">Socio</option>
                <option value="CLIENTE">Cliente</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">CRECI</label>
              <input
                type="text"
                value={infoForm.creci}
                onChange={(e) => setInfoForm(prev => ({ ...prev, creci: e.target.value }))}
                className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                  ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
                `}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">ID CVCRM</label>
            <input
              type="text"
              value={infoForm.cvcrmId}
              onChange={(e) => setInfoForm(prev => ({ ...prev, cvcrmId: e.target.value }))}
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all
                ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-gray-600' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}
              `}
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCloseInfoModal}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition
                ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={infoSaving || (!!infoForm.pracaId && infoAgencySearch.trim().length > 0 && filteredAgenciesForPraca.length === 0)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition
                ${infoSaving
                  ? (darkMode ? 'bg-vision-surface text-gray-600' : 'bg-gray-200 text-gray-400')
                  : (darkMode ? 'bg-[#294b29] hover:bg-green-600' : 'bg-[#294b29] hover:bg-green-600')
                }
              `}
            >
              {infoSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const syncModal = isSyncModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh]
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-lg font-bold">Sincronizar corretor</h3>
          <button
            onClick={() => setIsSyncModalOpen(false)}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Escolha os servicos para sincronizar. O CVCRM exige resultado unico.
          </p>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={syncOptions.whatsapp}
              onChange={(e) => setSyncOptions(prev => ({ ...prev, whatsapp: e.target.checked }))}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">WhatsApp</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Valida numero e atualiza foto do perfil.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={syncOptions.cvcrm}
              onChange={(e) => setSyncOptions(prev => ({ ...prev, cvcrm: e.target.checked }))}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">CVCRM</div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Busca o ID do corretor no CVCRM.
              </div>
            </div>
          </label>
          {syncResult?.error ? (
            <div className="text-sm text-red-500">{syncResult.error}</div>
          ) : null}
          {syncResult?.whatsapp ? (
            <div className={`text-xs ${syncResult.whatsapp.success && syncResult.whatsapp.valid ? 'text-green-500' : 'text-yellow-500'}`}>
              {syncResult.whatsapp.success
                ? (syncResult.whatsapp.valid ? 'WhatsApp valido.' : 'WhatsApp invalido.')
                : 'Falha ao validar WhatsApp.'}
            </div>
          ) : null}
          {syncResult?.cvcrm ? (
            <div className={`text-xs ${syncResult.cvcrm.status === 'ok' ? 'text-green-500' : 'text-yellow-500'}`}>
              {syncResult.cvcrm.status === 'ok'
                ? `CVCRM encontrado: ${syncResult.cvcrm.cvcrmId}`
                : syncResult.cvcrm.status === 'multiple'
                  ? 'Mais de um corretor encontrado. Defina o ID manualmente.'
                  : syncResult.cvcrm.status === 'not_found'
                    ? 'Nenhum corretor encontrado no CVCRM.'
                    : 'Falha ao capturar ID no CVCRM.'}
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-transparent">
          <button
            type="button"
            onClick={() => setIsSyncModalOpen(false)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition
              ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
            `}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRunSync}
            disabled={syncLoading || (!syncOptions.whatsapp && !syncOptions.cvcrm)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition
              ${syncLoading
                ? (darkMode ? 'bg-vision-surface text-gray-600' : 'bg-gray-200 text-gray-400')
                : (darkMode ? 'bg-[#294b29] hover:bg-green-600' : 'bg-[#294b29] hover:bg-green-600')
              }
            `}
          >
            {syncLoading ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      </div>
    </div>
  );

  const conversaModal = isConversaModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]
        ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
      `}>
        <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <h3 className="text-lg font-bold">Conversa do corretor</h3>
          <button
            onClick={() => setIsConversaModalOpen(false)}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5">
          {renderConversaPanel()}
        </div>
      </div>
    </div>
  );

  const ticketModal = ticketModalOpen && selectedTicket && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? 'bg-vision-darkGreen border border-vision-border text-white' : 'bg-white text-gray-900'}`}>
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className="text-sm font-bold">Chamado #{selectedTicket.id}</div>
            <span className={`text-[11px] px-2 py-1 rounded-full font-semibold ${getTicketStatusClasses(selectedTicket.status)}`}>
              {getTicketStatusLabel(selectedTicket.status)}
            </span>
          </div>
          <button
            onClick={closeTicketModal}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {ticketMessagesLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando mensagens...
            </div>
          ) : ticketMessagesError ? (
            <div className="text-sm text-red-500">{ticketMessagesError}</div>
          ) : ticketMessages.length === 0 ? (
            <div className="text-sm text-gray-500">Nenhuma mensagem registrada neste chamado.</div>
          ) : (
            ticketMessages.map(msg => (
              msg.direction === 'SYS' ? (
                <div key={msg.id} className="flex justify-center my-2">
                  <div className={`text-xs px-4 py-2 rounded-lg break-all text-center max-w-sm ${darkMode ? 'bg-vision-darkGreen text-gray-400 border border-vision-border' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                    {renderTicketMessageContent(msg)}
                  </div>
                </div>
              ) : (
                <div key={msg.id} className={`flex flex-col ${msg.direction === 'OUT' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`group relative max-w-[85%] md:max-w-[60%] px-3 py-2 text-sm sm:text-base rounded-2xl shadow-md transition-all duration-200
                      ${msg.direction === 'OUT'
                        ? darkMode
                          ? 'bg-vision-activeRed text-white rounded-br-none'
                          : 'bg-[#294b29] text-white rounded-br-none'
                        : darkMode
                          ? 'bg-gray-700/70 text-gray-100 rounded-bl-none'
                          : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                      }
                    `}
                  >
                    {renderTicketMessageContent(msg)}
                    <div className={`text-[10px] mt-1 flex justify-end ${msg.direction === 'OUT' ? 'text-white/60' : 'text-gray-400'}`}>
                      {formatTime(msg.createdAt)}
                    </div>
                  </div>
                </div>
              )
            ))
          )}
        </div>
      </div>
    </div>
  );

  const closeModal = closeModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-white"} w-full max-w-md max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
        <div className={`p-6 border-b flex-shrink-0 ${darkMode ? "border-vision-border" : "border-gray-100"}`}>
          <h3 className={`text-lg font-bold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-800"}`}>
            <CheckSquare size={20} className="text-green-500" />
            Finalizar Atendimento
          </h3>
        </div>
        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {closeError ? (
            <div className="text-sm text-red-500">{closeError}</div>
          ) : null}
          <div className="space-y-2">
            <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Produto vinculado <span className="text-red-500">*</span>
            </label>
            <select
              value={closeData.productId}
              onChange={e => setCloseData({ ...closeData, productId: e.target.value })}
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
            >
              <option value="">Selecione o produto</option>
              {closeProducts.map(p => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
            {!selectedBroker?.agencyId && (
              <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Vincule uma imobiliaria para sugerir os produtos corretos.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Categoria do chamado <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Tag size={16} className={`absolute left-3 top-3.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
              <select
                value={closeData.category}
                onChange={e => setCloseData({ ...closeData, category: e.target.value })}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
              >
                <option value="">Selecione uma categoria...</option>
                {closeCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Motivo / Resolucao
            </label>
            <textarea
              rows={4}
              value={closeData.reason}
              onChange={e => setCloseData({ ...closeData, reason: e.target.value })}
              placeholder="Descreva brevemente o que foi acordado..."
              className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all resize-none ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent"}`}
            />
          </div>
        </div>
        <div className={`p-4 flex justify-end gap-3 flex-shrink-0 ${darkMode ? "bg-black/20" : "bg-gray-50"}`}>
          <button
            onClick={() => setCloseModalOpen(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:bg-gray-200"}`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmitClose}
            disabled={!closeData.category || !closeData.productId || closeSubmitting}
            className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${!closeData.category || !closeData.productId || closeSubmitting ? "bg-gray-400 cursor-not-allowed" : darkMode ? "bg-[#294b29] hover:bg-green-800" : "bg-[#294b29] hover:bg-[#1a301a]"}`}
          >
            {closeSubmitting ? "Concluindo..." : "Concluir e Arquivar"}
          </button>
        </div>
      </div>
    </div>
  );

  const avatarModal = avatarPreviewUrl && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setAvatarPreviewUrl(null)}>
      <div className="relative max-w-[80vw] max-h-[80vh] rounded-2xl border border-white/10 bg-black/60 p-3">
        <img
          src={avatarPreviewUrl}
          alt="Foto do corretor"
          className="max-h-[70vh] max-w-[70vw] rounded-xl object-contain"
        />
      </div>
    </div>
  );

  // --- RENDER: LIST VIEW ---
  if (viewMode === 'list') {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-vision-green'}`}>
              Corretores
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Gerencie a equipe de vendas e parceiros.
            </p>
          </div>
        </div>

        {error && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${darkMode ? 'bg-red-900/40 text-red-300 border border-red-900/50' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {error}
          </div>
        )}

        {/* Content Card */}
        <div className={`rounded-xl border shadow-sm overflow-hidden flex flex-col ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          
          {/* Controls */}
          <div className={`p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
             <div className="relative w-full max-w-md">
               <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
               <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nome, email ou imobiliaria..." 
                  className={`
                    w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 outline-none text-sm transition-all
                    ${darkMode 
                      ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-[#294b29] focus:border-transparent'
                    }
                  `}
               />
             </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className={`text-xs uppercase tracking-wider ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                  <th className="px-6 py-4 font-semibold">Corretor</th>
                  <th className="px-6 py-4 font-semibold">Imobiliária</th>
                  <th className="px-6 py-4 font-semibold">Graduação</th>
                  <th className="px-6 py-4 font-semibold">Contatos</th>
                  <th className="px-6 py-4 font-semibold text-center">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-200'}`}>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      Carregando corretores...
                    </td>
                  </tr>
                ) : (
                  visibleBrokers.map((broker) => (
                    <tr 
                      key={broker.id} 
                      onClick={() => handleSelectBroker(broker)}
                      className={`
                        transition-colors cursor-pointer group
                        ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-50'}
                      `}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm overflow-hidden
                            ${darkMode ? 'bg-vision-surface text-gray-300' : 'bg-[#294b29]/10 text-[#294b29]'}
                          `}>
                            {broker.fotoUrl && !brokenPhotoIds.has(broker.id) ? (
                              <img
                                src={broker.fotoUrl}
                                alt={`Foto de ${broker.name}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                                onError={() => markPhotoAsBroken(broker.id)}
                              />
                            ) : (
                              broker.name.charAt(0)
                            )}
                          </div>
                          <div>
                             <p className={`font-medium group-hover:underline ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{broker.name}</p>
                             <p className="text-xs text-gray-500">Cargo: {formatCargoLabel(broker.cargo, Boolean(broker.agencyId))}</p>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-2">
                           <Building2 size={16} />
                           {broker.agency}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {broker.gamificacao && (
                          <GamificationBadge 
                            level={broker.gamificacao.nivel} 
                            points={broker.gamificacao.pontos}
                            size="sm"
                          />
                        )}
                      </td>
                      <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-2"><Phone size={12} /> {maskPhone(broker.phone)}</span>
                          <span className="flex items-center gap-2"><Mail size={12} /> {broker.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`
                           inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           ${broker.status === 'active' 
                             ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800')
                             : (darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800')
                           }
                        `}>
                          {broker.status === 'active' ? 'Ativo' : 'Bloqueado'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                         {/* Only Block Action as requested for the table */}
                         <button 
                           onClick={(e) => handleBlockBroker(e, broker.id)}
                           className={`
                             p-2 rounded-lg transition-colors inline-block
                             ${broker.status === 'blocked' 
                                ? (darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600') 
                                : (darkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-600')
                             }
                           `}
                           title={broker.status === 'active' ? "Bloquear Acesso" : "Desbloquear Acesso"}
                        >
                          <Ban size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {!isLoading && visibleBrokers.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                Nenhum corretor encontrado.
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Mostrando {visibleBrokers.length} de {totalCount} corretores
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1 || isLoading}
              className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition ${
                page <= 1 || isLoading
                  ? (darkMode ? 'border-vision-border text-gray-500' : 'border-gray-200 text-gray-400')
                  : (darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-300 text-gray-600 hover:bg-gray-100')
              }`}
            >
              Anterior
            </button>
            <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              Pagina {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || isLoading}
              className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition ${
                page >= totalPages || isLoading
                  ? (darkMode ? 'border-vision-border text-gray-500' : 'border-gray-200 text-gray-400')
                  : (darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-300 text-gray-600 hover:bg-gray-100')
              }`}
            >
              Proxima
            </button>
          </div>
        </div>

        {brokerModal}
      </div>
    );
  }

  if (viewMode === 'detail' && !selectedBroker) {
    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        <div className="flex items-center gap-3">
          <Loader2 className={`w-6 h-6 animate-spin ${darkMode ? 'text-white' : 'text-[#294b29]'}`} />
          <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando perfil...</span>
        </div>
      </div>
    );
  }

  if (viewMode === 'detail' && selectedBroker) {
    const isWhatsappInvalid = selectedBroker.possuiWhatsapp === false;
    const chamadosValue = brokerKpis
      ? String(brokerKpis.chamadosTotal ?? brokerKpis.chamadosAbertos ?? 0)
      : (brokerKpisLoading ? '...' : '');
    const leadsValue = brokerKpis?.leadsRecebidos != null ? String(brokerKpis.leadsRecebidos) : '';
    const visitasValue = brokerKpis?.visitas != null ? String(brokerKpis.visitas) : '';
    const vgvValue = brokerKpis?.vgv != null ? String(brokerKpis.vgv) : '';

    const stats = [
      { label: 'Chamados', value: chamadosValue, icon: MessageCircle, color: 'text-amber-500', bg: 'bg-amber-500/10' },
      { label: 'Leads recebidos', value: leadsValue, icon: Target, color: 'text-blue-500', bg: 'bg-blue-500/10' },
      { label: 'Visitas', value: visitasValue, icon: Zap, color: 'text-purple-500', bg: 'bg-purple-500/10' },
      { label: 'VGV', value: vgvValue, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    ];

    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-6">
            <div className={`
              p-6 rounded-2xl shadow-sm border text-center relative
              ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
            `}>
              <div className="absolute right-4 top-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFichaModalOpen(true)}
                  className={`h-8 w-8 rounded-full transition-all flex items-center justify-center
                    ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                  `}
                  title="Ver ficha"
                  aria-label="Ver ficha"
                >
                  <FileText size={14} />
                </button>
                <button
                  type="button"
                  onClick={handleOpenSyncModal}
                  className={`h-8 w-8 rounded-full transition-all flex items-center justify-center
                    ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                  `}
                  title="Sincronizar"
                  aria-label="Sincronizar"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              <div className={`
                w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 text-4xl font-bold border-4 overflow-hidden
                ${darkMode ? 'bg-vision-surface text-gray-400 border-vision-border' : 'bg-[#294b29]/10 text-[#294b29] border-white shadow-lg'}
              `}>
                {selectedBroker.fotoUrl && !brokenPhotoIds.has(selectedBroker.id) ? (
                  <button
                    type="button"
                    onClick={() => setAvatarPreviewUrl(selectedBroker.fotoUrl as string)}
                    className="w-full h-full"
                    aria-label="Ampliar foto do corretor"
                  >
                    <img
                      src={selectedBroker.fotoUrl}
                      alt={`Foto de ${selectedBroker.name}`}
                      className="w-full h-full object-cover"
                      onError={() => markPhotoAsBroken(selectedBroker.id)}
                    />
                  </button>
                ) : (
                  selectedBroker.name.charAt(0)
                )}
              </div>
              <h2 className="text-2xl font-bold">{selectedBroker.name}</h2>
              <div className="group relative mx-auto mt-3 h-20 w-fit">
                <img
                  src={calcaBranca}
                  alt="Calca branca"
                  className="h-20 w-auto object-contain"
                />
                <div className="pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide shadow-lg border
                    ${darkMode ? 'bg-vision-surface text-[#39ff14] border-vision-border' : 'bg-white text-[#294b29] border-gray-200'}
                  `}>
                    {selectedBroker.gamificacao?.pontos ?? 0}
                  </span>
                </div>
              </div>
              <div className="relative mt-6 group">
                <button
                  type="button"
                  disabled={isWhatsappInvalid}
                  onClick={() => {
                    if (isWhatsappInvalid) return;
                    setProfileTab('conversa');
                    setIsConversaModalOpen(true);
                  }}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs transition-all active:scale-95
                    ${isWhatsappInvalid
                      ? (darkMode ? 'bg-red-900/40 text-red-300 cursor-not-allowed' : 'bg-red-100 text-red-600 cursor-not-allowed')
                      : (darkMode ? 'bg-vision-surface hover:bg-vision-border text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800')
                    }
                  `}
                >
                  Conversar
                </button>
                {isWhatsappInvalid ? (
                  <span className={`pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100 text-[11px] font-semibold px-2.5 py-1 rounded-full border shadow-lg
                    ${darkMode ? 'bg-vision-surface text-red-300 border-vision-border' : 'bg-white text-red-600 border-red-200'}
                  `}>
                    WhatsApp invalido
                  </span>
                ) : null}
              </div>
              {getAgencyWarningLabel(selectedBroker) ? (
                <div className={`mt-4 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full border
                  ${darkMode ? 'border-yellow-500/40 text-yellow-300 bg-yellow-900/20' : 'border-yellow-200 text-yellow-700 bg-yellow-50'}
                `}>
                  <AlertTriangle size={12} />
                  {getAgencyWarningLabel(selectedBroker)}
                </div>
              ) : null}

            </div>

            <div className={`
              p-6 rounded-2xl shadow-sm border
              ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
            `}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">Informações</h3>
                <button
                  type="button"
                  onClick={handleOpenInfoModal}
                  className={`h-8 w-8 rounded-full transition-all flex items-center justify-center
                    ${darkMode ? 'bg-vision-surface text-gray-300 hover:bg-vision-border' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                  `}
                  title="Editar"
                  aria-label="Editar"
                >
                  <Pencil size={14} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className={`${darkMode ? 'bg-vision-surface' : 'bg-gray-50'} p-2 rounded-lg`}>
                    <Building2 size={18} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Imobiliaria</p>
                    <p className="text-sm font-semibold">{renderAgencyLabel(selectedBroker)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className={`${darkMode ? 'bg-vision-surface' : 'bg-gray-50'} p-2 rounded-lg`}>
                    <FileBadge size={18} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Cargo</p>
                    <p className="text-sm font-semibold">{formatCargoLabel(selectedBroker.cargo, Boolean(selectedBroker.agencyId))}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className={`${darkMode ? 'bg-vision-surface' : 'bg-gray-50'} p-2 rounded-lg`}>
                    <User size={18} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">CRECI</p>
                    <p className="text-sm font-semibold">{selectedBroker.creci || 'Nao informado'}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-dashed border-gray-500/20">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Bio</p>
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedBroker.bio || 'Nenhuma informacao adicional.'}
                  </p>
                </div>
              </div>
            </div>

            <div className={`
              p-5 rounded-2xl shadow-sm border
              ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
            `}>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Tags</h3>
                <button
                  type="button"
                  onClick={() => {
                    setBrokerTags([]);
                    setTagSuccess(null);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${
                    darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Limpar
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {brokerTags.length > 0 ? (
                  brokerTags.map((tag) => (
                    <span
                      key={`${tag.id}-${tag.name}`}
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                        darkMode ? 'border-vision-border text-gray-200' : 'border-gray-200 text-gray-700'
                      }`}
                    >
                      {tag.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag.name)}
                        className={`p-0.5 rounded-full ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
                        aria-label={`Remover tag ${tag.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))
                ) : (
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Sem tags</span>
                )}
              </div>

              <div className="mt-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag(tagInput);
                    }
                  }}
                  placeholder="Adicionar tag..."
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-gray-50 border-gray-200'
                  }`}
                />
                {tagSuggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tagSuggestions.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleAddTag(tag.name)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                          darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        + {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {(tagError || tagSuccess) && (
                <p className={`text-xs mt-3 ${tagError ? 'text-red-500' : 'text-green-600'}`}>
                  {tagError || tagSuccess}
                </p>
              )}

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveTags}
                  disabled={!isTagsDirty || tagSaving}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow ${
                    !isTagsDirty || tagSaving
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#294b29] hover:bg-[#1f371f]'
                  }`}
                >
                  {tagSaving ? 'Salvando...' : 'Salvar'}
                </button>
                {!isTagsDirty && (
                  <span className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Tudo salvo</span>
                )}
              </div>
            </div>
          </div>

          <div className={`
            lg:col-span-2 p-6 rounded-2xl shadow-sm border
            ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
          `}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className={`p-5 rounded-2xl border shadow-sm flex flex-col gap-3 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-100'}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`}>
                    <stat.icon size={20} />
                  </div>
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {stat.label}
                    </p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
            {renderBrokerTimelinePanel()}
          </div>
        </div>
        {brokerModal}
        {conversaModal}
        {infoModal}
        {syncModal}
        {fichaModal}
        {ticketModal}
        {closeModal}
        {avatarModal}
      </div>
    );
  }

  return null;
};

